import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessKidneyStones } from '@hooks/register/screening/use-assess-kidney-stones';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessKidneyStonesSchema } from '@views/register/screening/assess-kidney-stones.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import { asString, asStringBool } from '@/utils/parse';
import type { IAssessKidneyStonesFormValues, IAssessKidneyStonesModel, IAssessKidneyStonesSubmitResult } from '@/types/screening/assess-kidney-stones';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

// Episode counts (5 represents "5+").
const EPISODE_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5+' },
];

// Nkr.Model.Enums.MedicalAssessments.RemissionStatus (value → default English label).
const REMISSION_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Less than 1 year' },
    { value: 1, label: '1 year' },
    { value: 2, label: '2 years' },
    { value: 3, label: '3 years' },
    { value: 4, label: '4 years' },
    { value: 5, label: '5 years' },
    { value: 6, label: '6 years' },
    { value: 7, label: '7 years' },
    { value: 8, label: '8 years' },
    { value: 9, label: '9 years' },
    { value: 10, label: '10 years' },
    { value: 11, label: '11 years' },
    { value: 12, label: '12 years' },
    { value: 13, label: '13 years' },
    { value: 14, label: '14 years' },
    { value: 15, label: '15+ years' },
];

// Nkr.Model.Enums.MedicalAssessments.KidneyStoneTreatment (value → default English label).
const TREATMENT_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: 'With medical intervention' },
    { value: 2, label: 'With surgical intervention' },
    { value: 3, label: "I don't know" },
];

const AssessKidneyStones = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessKidneyStones(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessKidneyStonesContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessKidneyStonesContentProps {
    model: IAssessKidneyStonesModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessKidneyStonesFormValues) => Promise<IAssessKidneyStonesSubmitResult>;
}

const AssessKidneyStonesContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessKidneyStonesContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessKidneyStonesFormValues, IAssessKidneyStonesSubmitResult>({
        resolver: zodResolver(assessKidneyStonesSchema),
        defaultValues: {
            hasHadKidneyStones: asStringBool(model.hasHadKidneyStones),
            kidneyStoneEpisodes: asString(model.kidneyStoneEpisodes),
            kidneyStoneRemissionStatus: asString(model.kidneyStoneRemissionStatus),
            kidneyStoneTreatment: asString(model.kidneyStoneTreatment),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = form;

    const showStatus = watch('hasHadKidneyStones') === '1';

    useEffect(() => {
        if (!showStatus) {
            setValue('kidneyStoneEpisodes', '');
            setValue('kidneyStoneRemissionStatus', '');
            setValue('kidneyStoneTreatment', '');
        }
    }, [showStatus, setValue]);

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessKidneyStones}
            title={t('DashLabel_ProgressKidneyStones', 'Kidney Stones')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_HaveYouEverBeenDiagnosedWithKidneyStones',
                        'Have you ever been diagnosed with kidney stones',
                    )}
                    ?<FieldError message={errors.hasHadKidneyStones?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="1"
                            {...register('hasHadKidneyStones')}
                        />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="0"
                            {...register('hasHadKidneyStones')}
                        />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {showStatus && (
                <div className="sub-question" id="status">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_HowManyTimesHaveYouHadKidneyStones',
                                'How many times have you had kidney stones',
                            )}
                            ?<FieldError message={errors.kidneyStoneEpisodes?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            {EPISODE_OPTIONS.map((option) => (
                                <label className="radio-inline" key={option.value}>
                                    <input
                                        type="radio"
                                        value={option.value}
                                        {...register('kidneyStoneEpisodes')}
                                    />{' '}
                                    {option.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_HowLongSinceYourLastEpisode',
                                'How long since your last episode',
                            )}
                            ?
                            <FieldError
                                message={errors.kidneyStoneRemissionStatus?.message}
                            />
                        </label>
                        <div className="col-xs-12 col-md-2" style={{ marginLeft: 10 }}>
                            <select
                                className="form-control"
                                {...register('kidneyStoneRemissionStatus')}
                            >
                                <option value="">
                                    {t('DashLabel_PleaseSelect', 'Please Select')}
                                </option>
                                {REMISSION_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {t(`Enum_RemissionStatus_${option.value}`, option.label)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_HowWereTheMostRecentStonesTreated',
                                'How were the most recent stones treated',
                            )}
                            ?
                        </label>
                        <div className="col-xs-12 col-md-6" style={{ marginLeft: 10 }}>
                            <select
                                className="form-control"
                                {...register('kidneyStoneTreatment')}
                            >
                                <option value="">
                                    {t('DashLabel_PleaseSelect', 'Please Select')}
                                </option>
                                {TREATMENT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {t(
                                            `Enum_KidneyStoneTreatment_${option.value}`,
                                            option.label,
                                        )}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </StepFormLayout>
    );
};

export default AssessKidneyStones;
